import { Link } from "react-router-dom";
import { VotingMechanicsDemo } from "../components/ui";

/** Marginal quadratic cost: piecewise steps showing v² growth intuition */
function QuadraticCostCurveSvg() {
	return (
		<svg
			viewBox="0 0 320 200"
			className="mx-auto h-auto w-full max-w-md text-wi-gold"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.15"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<title>Cumulative cost grows faster as votes accumulate</title>
			<rect x="10" y="10" width="300" height="180" rx="12" fill="var(--color-wi-surface)" stroke="currentColor" strokeOpacity="0.22" />
			<line x1="44" y1="160" x2="280" y2="160" opacity="0.35" />
			<line x1="44" y1="36" x2="44" y2="160" opacity="0.35" />
			<text x="50" y="28" className="fill-wi-text font-geist text-[10px] font-medium" stroke="none">
				Cumulative cost vs votes on one option
			</text>
			<polyline
				points="44,160 80,150 116,128 152,96 188,56 224,40"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				opacity="0.9"
			/>
			<text x="232" y="48" className="fill-wi-text/55 font-geist text-[9px]" stroke="none">
				steeper ↑
			</text>
			<text x="250" y="172" className="fill-wi-text/50 font-geist text-[9px]" textAnchor="end" stroke="none">
				votes
			</text>
			<text x="22" y="100" className="fill-wi-text/50 font-geist text-[9px]" textAnchor="middle" transform="rotate(-90 22 100)" stroke="none">
				Σ marginal
			</text>
		</svg>
	);
}

const GLOSSARY: { term: string; def: string }[] = [
	{ term: "Borda count", def: "A ranked rule: each option’s points depend on how high it sits in a voter’s list; higher ranks earn more points." },
	{ term: "Rank", def: "Position in the ordered list after the voter finishes (1 = top). Not a “score” until mapped through Borda." },
	{ term: "Mean Borda (chart)", def: "Server-side aggregation averages Borda points per option across voters for chart-friendly series." },
	{ term: "Joint winners", def: "When tallies tie, the UI treats winners equally — no silent tie-break; a dashed “Tied” guide may appear after normalization." },
	{ term: "Normalization (0–100)", def: "Chart Y axis scales so the leading option(s) reach 100 after scaling, making relative gaps legible at a glance." },
	{ term: "Tied band (2%)", def: "If any two options are within two percentage points after normalization, the chart shows a horizontal dashed tie guide." },
	{ term: "Quadratic budget", def: "Voters share a fixed pool (100 points in the shipped poll); spending must satisfy Σ v² ≤ 100 across options." },
	{ term: "Marginal cost (quadratic)", def: "Each +1 vote on an option increases total quadratic cost by (2v + 1) in integer steps — the “v²” friction users feel." },
	{ term: "Curator / Curator Link", def: "Private capability URL for admin actions; stored hashed at rest — see Privacy and Security pages." },
];

export function MathPage() {
	return (
		<div className="wi-editorial-prose space-y-12 pb-24">
			<header className="space-y-4">
				<p className="font-geist text-wi-text/50 text-xs uppercase tracking-[0.16em]">WorthyIdea · Numbers</p>
				<h1 className="font-newsreader text-wi-text text-[2rem] leading-[1.1] tracking-tight sm:text-[2.65rem]">
					The math behind the poll
				</h1>
					<p className="font-geist text-[0.9rem] leading-relaxed text-wi-text/75 sm:text-[0.95rem]">
						In plain terms: ranked choice turns an ordered list into points, and weighted picks make extra intensity
						more expensive — so the room’s preference is readable without one voter buying the chart. The sections
						below show the formulas. For product voice and Phase 1 scope, start at{" "}
						<Link to="/about" className="font-medium text-wi-gold underline-offset-2 hover:underline">
							About
						</Link>
						.
					</p>
				</header>

				<section className="space-y-4" aria-label="Interactive voting demo">
					<VotingMechanicsDemo />
				</section>

				<section className="space-y-4">
					<h2 className="font-newsreader text-wi-text text-2xl tracking-tight sm:text-3xl">Borda — intuition</h2>
				<div className="space-y-3 font-geist text-[0.9rem] leading-relaxed text-wi-text/80 sm:text-[0.9375rem]">
					<p>
						With <strong className="text-wi-text">n</strong> options, label ranks <strong className="text-wi-text">1 … n</strong>{" "}
						from best to worst. The shipped poll awards{" "}
						<strong className="text-wi-text">
							n − r + 1
						</strong>{" "}
						points to the option sitting at rank <strong className="text-wi-text">r</strong>. So the top item gets{" "}
						<strong className="text-wi-text">n</strong> points, the next gets <strong className="text-wi-text">n − 1</strong>,
						and the last gets <strong className="text-wi-text">1</strong>.
					</p>
					<p>
						Why it feels fair: burying a candidate at the bottom still grants a single point, but broad support — many voters
						keeping an option high — accumulates faster than fringe enthusiasm scattered across the middle.
					</p>
					<p className="rounded-xl border border-wi-text/10 bg-wi-surface/60 px-4 py-3 font-geist text-[0.8125rem] text-wi-text/72">
						<strong className="text-wi-text">Logic Ledger</strong> in the voter UI previews this ladder for the current{" "}
						<strong className="text-wi-text">n</strong>; the authoritative tally is computed when the Curator finalizes.
					</p>
				</div>
			</section>

			<section className="space-y-4 border-wi-text/10 border-t pt-10">
				<h2 className="font-newsreader text-wi-text text-2xl tracking-tight sm:text-3xl">Ties and charts</h2>
				<div className="space-y-3 font-geist text-[0.9rem] leading-relaxed text-wi-text/80 sm:text-[0.9375rem]">
					<p>
						After finalize, options can <strong className="text-wi-text">tie</strong>. The interface treats{" "}
						<strong className="text-wi-text">joint winners</strong> as joint winners — no arbitrary tie-break in the chart.
					</p>
					<p>
						Charts normalize scores to a <strong className="text-wi-text">0–100</strong> vertical range so relative strength
						is readable. If <strong className="text-wi-text">any two</strong> series land within{" "}
						<strong className="text-wi-text">2%</strong> after that normalization, a horizontal dashed &quot;Tied&quot; guide
						reinforces near-ties beside the gold spotlight.
					</p>
				</div>
			</section>

			<section className="space-y-4 border-wi-text/10 border-t pt-10">
				<h2 className="font-newsreader text-wi-text text-2xl tracking-tight sm:text-3xl">Quadratic cost</h2>
				<div className="space-y-3 font-geist text-[0.9rem] leading-relaxed text-wi-text/80 sm:text-[0.9375rem]">
					<p>
						Each option receives integer votes <strong className="text-wi-text">vᵢ ≥ 0</strong>. Total quadratic cost is{" "}
						<strong className="text-wi-text">Σᵢ vᵢ²</strong>. The poll fixes a <strong className="text-wi-text">100</strong>{" "}
						point budget and enforces <strong className="text-wi-text">Σᵢ vᵢ² ≤ 100</strong> server-side.
					</p>
					<p>
						Marginal cost for adding one vote to option <strong className="text-wi-text">i</strong> when it already holds{" "}
						<strong className="text-wi-text">v</strong> is{" "}
						<strong className="text-wi-text">(v + 1)² − v² = 2v + 1</strong> — cheap at the start, expensive when you
						push hard on a single choice. That is the mathematical reason intensity is visible but cannot silently dominate.
					</p>
					<figure className="py-2">
						<QuadraticCostCurveSvg />
					</figure>
				</div>
			</section>

			<section className="space-y-4 border-wi-text/10 border-t pt-10">
				<h2 className="font-newsreader text-wi-text text-2xl tracking-tight sm:text-3xl">Aggregation note</h2>
				<p className="font-geist text-[0.9rem] leading-relaxed text-wi-text/80 sm:text-[0.9375rem]">
					For quadratic charts, pick one documented aggregate per option (for example <strong className="text-wi-text">Σ v</strong>{" "}
					across voters, or <strong className="text-wi-text">Σ v²</strong>) and keep it consistent in code and copy. The
					exact choice should match whatever the implementation exports in finalize — this page describes the user-visible
					<strong className="text-wi-text"> v² </strong>
					friction, not a second hidden scoring scheme.
				</p>
			</section>

				<section className="space-y-5 border-wi-text/10 border-t pt-10">
					<h2 className="font-newsreader text-wi-text text-2xl tracking-tight sm:text-3xl">Glossary</h2>
					<dl className="divide-y divide-wi-text/10 rounded-xl border border-wi-text/10 bg-wi-surface/40">
						{GLOSSARY.map((row) => (
							<div key={row.term} className="px-4 py-3.5 sm:px-5">
								<dt className="font-newsreader text-wi-text text-base tracking-tight">{row.term}</dt>
								<dd className="mt-1.5 font-geist text-[0.8125rem] leading-relaxed text-wi-text/75">{row.def}</dd>
							</div>
						))}
					</dl>
				</section>

				<section className="space-y-3 border-wi-text/10 border-t pt-10">
					<h2 className="font-newsreader text-wi-text text-2xl tracking-tight sm:text-3xl">Further reading</h2>
					<p className="font-geist text-[0.9rem] leading-relaxed text-wi-text/75 sm:text-[0.9375rem]">
						Wikipedia is a solid next stop after this friendly on-ramp:
					</p>
					<ul className="space-y-1.5 font-geist text-sm text-wi-text/85">
						<li>
							<a
								href="https://en.wikipedia.org/wiki/Borda_count"
								className="font-medium text-wi-gold underline-offset-2 hover:underline"
								target="_blank"
								rel="noopener noreferrer"
							>
								Borda count
							</a>
						</li>
						<li>
							<a
								href="https://en.wikipedia.org/wiki/Quadratic_voting"
								className="font-medium text-wi-gold underline-offset-2 hover:underline"
								target="_blank"
								rel="noopener noreferrer"
							>
								Quadratic voting
							</a>
						</li>
					</ul>
				</section>
			</div>
		);
	}
